// Company sign-in (SSO) and MFA helpers shared by the sign-in pages, the
// Company sign-in settings tab and the user screens.

export const USER_TYPE_LOCAL = 1;
export const USER_TYPE_SSO = 2;
export const MATCH_BY_EMAIL = 1;
export const MATCH_BY_USERNAME = 2;
export const PROTOCOL_SAML = 1;
export const PROTOCOL_OIDC = 2;
export const MFA_STEP_REQUIRED = "MfaRequired";
export const MFA_STEP_ENROL = "MfaEnrolmentRequired";

// Company sign-in is on and password sign-in is off: the sign-in pages go
// straight to the company's sign-in page.
export const isPasswordSignInOff = (enterprise) =>
  Boolean(enterprise?.ssoEnabled) && enterprise?.allowPasswordSignIn === false;

// Set when the person signs out in this tab, so the sign-in pages wait for a
// click instead of going to the company's sign-in page, which would sign them
// straight back in while their session there is still open.
const SIGNED_OUT_KEY = "edexlms.ssoSignedOut";

export const markSignedOut = () => {
  try {
    window.sessionStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch (error) {
    // Storage can be unavailable (private mode); the pages then go straight on.
  }
};

export const clearSignedOut = () => {
  try {
    window.sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch (error) {
    // As above
  }
};

export const isSignedOut = () => {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const leavesTheApp = (path) =>
  path.startsWith("//") ||
  path.includes("\\") ||
  path.includes("://") ||
  [...path].some((character) => character.charCodeAt(0) < 32);

// A path inside the app ("/learning-journey"), or null for anything that
// could leave it: another host ("//host", "/\host" and their encoded forms),
// a scheme, a backslash or a control character.
export const safeTarget = (target) => {
  if (typeof target !== "string") {
    return null;
  }
  const value = target.trim();
  if (!value.startsWith("/") || value.length > 500) {
    return null;
  }
  let decoded;
  try {
    decoded = decodeURIComponent(value);
  } catch (error) {
    return null;
  }
  return leavesTheApp(value) || leavesTheApp(decoded) ? null : value;
};

// The API address that starts a company sign-in for the organisation.
export const ssoStartUrl = (subDomain, target) => {
  const url = new URL(
    `/api/sso/${encodeURIComponent(subDomain)}/start`,
    process.env.REACT_APP_URL
  );
  const path = safeTarget(target);
  if (path) {
    url.searchParams.set("target", path);
  }
  return url.toString();
};

// A full-page navigation: the company's sign-in page is another site.
export const redirectTo = (url) => {
  window.location.assign(url);
};

export const defaultUserType = (enterprise) =>
  isPasswordSignInOff(enterprise) ? USER_TYPE_SSO : USER_TYPE_LOCAL;

// While people are matched by username, an SSO user may have no email.
export const isEmailOptional = (enterprise, userType) =>
  Number(userType) === USER_TYPE_SSO && enterprise?.ssoMatchBy === MATCH_BY_USERNAME;

// A connection exists (switched on or not): users can be made SSO users,
// for example before the first Test connection.
export const isSsoSetUp = (enterprise) =>
  enterprise?.ssoMatchBy !== null && enterprise?.ssoMatchBy !== undefined;

export const USER_TYPE_OPTIONS = [
  { id: USER_TYPE_LOCAL, value: USER_TYPE_LOCAL, name: "user_type_local", i18n: true },
  { id: USER_TYPE_SSO, value: USER_TYPE_SSO, name: "user_type_sso", i18n: true },
];

// "JBSWY3DPEHPK3PXP" as "JBSW Y3DP EHPK 3PXP", easier to type into an app.
export const formatKey = (key) =>
  (key || "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();

const SSO_ERROR_MESSAGES = {
  "not-set-up": "sso_error_not_set_up",
  unavailable: "sso_error_unavailable",
  expired: "sso_error_expired",
};

export const ssoErrorMessageId = (reason) =>
  SSO_ERROR_MESSAGES[reason] || "sso_error_unavailable";

// Pasted PEM certificates, one per entry.
export const splitCertificates = (text) =>
  (text || "")
    .split(/(?=-----BEGIN CERTIFICATE-----)/)
    .map((certificate) => certificate.trim())
    .filter(Boolean);

const trimmedOrNull = (value) => (typeof value === "string" && value.trim()) || null;

// PUT /enterprise/sso from the Company sign-in form. A blank client secret
// keeps the saved one; no certificates keep the saved ones.
export const ssoPayload = (values) => {
  const protocol = values.protocol?.id ?? values.protocol;
  const matchBy = values.matchBy?.id ?? values.matchBy ?? MATCH_BY_EMAIL;
  return {
    protocol,
    isEnabled: Boolean(values.isEnabled),
    allowPasswordSignIn: values.allowPasswordSignIn !== false,
    matchBy,
    usernameClaim: trimmedOrNull(values.usernameClaim),
    samlMetadataUrl: trimmedOrNull(values.samlMetadataUrl),
    samlIdpEntityId: trimmedOrNull(values.samlIdpEntityId),
    samlSignOnUrl: trimmedOrNull(values.samlSignOnUrl),
    samlCertificates: splitCertificates(values.samlCertificates),
    oidcIssuer: trimmedOrNull(values.oidcIssuer),
    oidcClientId: trimmedOrNull(values.oidcClientId),
    oidcClientSecret: values.oidcClientSecret || null,
    // Only an OIDC connection matching by email can trust the provider's email addresses
    oidcTrustEmail:
      Number(protocol) === PROTOCOL_OIDC && Number(matchBy) === MATCH_BY_EMAIL && Boolean(values.oidcTrustEmail),
    allowPortalSignIn: values.allowPortalSignIn !== false,
  };
};

export const daysUntil = (date, now = new Date()) =>
  Math.floor((new Date(date).getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
